/**
 * ModuleWorkspace — sdílená stránka pro widget-first modul.
 *
 * Bere definici z module-ui-catalog (tabs → ModulePanel).
 * Phone: taby = horní carousel (phone-swipe-tabs), 1 panel najednou.
 */
import { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PageHeader, Tab, TabList, TabPanel, Tabs } from '../../components/ui';
import { useIsPhoneLayout } from '../../lib/ui-layout';
import { ModulePanel } from '../../widgets/ModulePanel';
import type { ModuleUiDef } from '../../widgets/module-ui-catalog';
import { cn } from '../../lib/utils';

export interface ModuleWorkspaceProps {
  def: ModuleUiDef;
  /** Override title (i18n už aplikované). */
  title?: string;
  description?: string;
  icon?: LucideIcon;
  defaultTab?: string;
  /**
   * Cílový nod pro všechny panely (Node workspace).
   * undefined = běžná modulová stránka (NodePicker session-only uvnitř panelu).
   */
  forceNode?: string | null;
  /** Změna nodu z pickeru uvnitř panelu (Node workspace → navigace). */
  onNodeChange?: (node: string | null) => void;
  /** Skrýt PageHeader (když ho kreslí parent, např. Node workspace). */
  hideHeader?: boolean;
}

export function ModuleWorkspace({
  def,
  title,
  description,
  icon,
  defaultTab,
  forceNode,
  onNodeChange,
  hideHeader,
}: ModuleWorkspaceProps) {
  const { t } = useTranslation();
  const phone = useIsPhoneLayout();
  const first = def.tabs[0]?.id ?? '';
  const resolvedDefault = defaultTab && def.tabs.some((x) => x.id === defaultTab)
    ? defaultTab
    : first;
  const [phoneTab, setPhoneTab] = useState(resolvedDefault);
  // URL/tab prop (např. /modules/codable/settings) musí přepnout i po mountu —
  // Tabs defaultTab se jinak drží na první hodnotě (settings „nefunguje“).
  useEffect(() => {
    if (resolvedDefault) setPhoneTab(resolvedDefault);
  }, [resolvedDefault]);
  const headerTitle = title ?? (def.titleKey ? t(def.titleKey, def.title) : def.title);
  const headerDesc = description ??
    (def.descriptionKey && def.description
      ? t(def.descriptionKey, def.description)
      : def.description);
  const HeaderIcon = icon ?? def.icon;

  const activeTab = def.tabs.some((x) => x.id === phoneTab)
    ? phoneTab
    : first;

  if (phone) {
    const tab = def.tabs.find((x) => x.id === activeTab) ?? def.tabs[0];
    return (
      <div className='space-y-3'>
        {!hideHeader && (
          <div className='px-0.5'>
            <div className='flex items-center gap-2'>
              {HeaderIcon && <HeaderIcon size={16} className='text-plex shrink-0' />}
              <h1 className='text-base font-semibold text-gray-100 truncate'>{headerTitle}</h1>
            </div>
            {headerDesc && (
              <p className='text-xs text-gray-500 mt-0.5 line-clamp-2'>{headerDesc}</p>
            )}
          </div>
        )}
        {def.tabs.length > 1 && (
          <>
            <div className='phone-card-carousel'>
              {def.tabs.map((tb) => {
                const Icon = tb.icon;
                const label = tb.labelKey ? t(tb.labelKey, tb.label) : tb.label;
                return (
                  <button
                    key={tb.id}
                    type='button'
                    className={cn('phone-card-chip', tb.id === activeTab && 'is-active')}
                    onClick={() => setPhoneTab(tb.id)}
                  >
                    {Icon ? <Icon size={13} className='shrink-0 opacity-80' /> : null}
                    <span className='truncate'>{label}</span>
                  </button>
                );
              })}
            </div>
            <div className='phone-swipe-dots'>
              {def.tabs.map((tb) => (
                <span
                  key={tb.id}
                  className={cn('phone-swipe-dot', tb.id === activeTab && 'is-active')}
                />
              ))}
            </div>
          </>
        )}
        {tab && (
          <ModulePanel
            panel={tab.panel}
            forceNode={forceNode}
            onNodeChange={onNodeChange}
            preferredModule={def.module}
          />
        )}
      </div>
    );
  }

  return (
    <div className='space-y-5'>
      {!hideHeader && (
        <PageHeader
          title={headerTitle}
          description={headerDesc}
          icon={HeaderIcon}
        />
      )}
      {/* key = remount při změně defaultTab z URL (settings path / ?tab=) */}
      <Tabs key={resolvedDefault || first} defaultTab={resolvedDefault || first}>
        <TabList>
          {def.tabs.map((tab) => {
            const Icon = tab.icon;
            const label = tab.labelKey ? t(tab.labelKey, tab.label) : tab.label;
            return (
              <Tab key={tab.id} id={tab.id} icon={Icon ? <Icon size={14} /> : undefined}>
                {label}
              </Tab>
            );
          })}
        </TabList>
        {def.tabs.map((tab) => (
          <TabPanel key={tab.id} id={tab.id}>
            <ModulePanel
              panel={tab.panel}
              forceNode={forceNode}
              onNodeChange={onNodeChange}
              preferredModule={def.module}
            />
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
